import React from "react";
import locale from "@bonliva-crm/locale";
import { useObserver } from "mobx-react";
import {
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Clear";
import { ThemeType } from "@theme";
import { Checkbox, Select, Text } from "@components/formx";
import { RadiographersStore, Specialization } from "./RadiographersStore";
import { ExpMultiSelect, ExpTopPanel, OutlinedInput } from "../Common";

type Props = {
  profession: string;
  store: RadiographersStore;
};

export function RadiographersExpertise(props: Props) {
  const radiographersStore = props.store;
  const profItem = `professions.${radiographersStore.storeIndex}`;
  const classes = useStyles({});

  return useObserver(() => (
    <>
      <Paper className={classes.paper}>
        <ExpTopPanel
          certifiedPath={`${profItem}.certifiedFrom`}
          certifiedFromTxt={`${radiographersStore.prof} sedan`}
          onDeleteClick={radiographersStore.deleteExpertise}
        />

        <ExpMultiSelect
          name={locale.AddExp}
          selectOptions={radiographersStore.selectSpecializationList}
          onChange={(val) => radiographersStore.setSelectedSpecialization(val)}
          selectedVal={radiographersStore.selectedSpecialization}
          btnClick={(_) => radiographersStore.addSpec()}
          btnDisabled={!radiographersStore.selectedSpecialization}
        />
      </Paper>

      {radiographersStore.specializations.length !== 0 && (
        <SpecializationTable
          name={locale.Experienced}
          profItem={`${profItem}.specializations`}
          profession={radiographersStore.profession}
          items={radiographersStore.specializations}
          deleteRow={radiographersStore.removeSpec}
        />
      )}

      <Paper style={{ padding: 16 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Select
              label={locale["Known systems"]}
              name={`${profItem}.systems`}
              multi
              options={radiographersStore.systems.map((x) => ({
                label: x,
                value: x
              }))}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Select
              label={locale.Machines}
              name={`${profItem}.machines`}
              multi
              options={radiographersStore.machines.map((x) => ({
                label: x,
                value: x
              }))}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  ));
}

type SpecializationTableProps = {
  name: string;
  profession: string;
  profItem: string;
  items: Specialization[];
  deleteRow: (path: string, i: number) => void;
};

function SpecializationTable(props: SpecializationTableProps) {
  const { items, profItem, deleteRow, profession } = props;

  return useObserver(() => (
    <Paper>
      <Grid item xs={12}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ width: "15%" }}>
                {locale.Specialization}
              </TableCell>

              <TableCell align="center" style={{ width: 64 }}>
                {locale.Experienced}
              </TableCell>

              <TableCell align="center" style={{ width: "10rem" }}>
                {locale.ExperienceYearFrom}
              </TableCell>

              <TableCell align="center" style={{ width: 64, whiteSpace: "nowrap" }}>
                {locale.Interested}
              </TableCell>

              <TableCell align="center">{locale.WriteComment}</TableCell>

              <TableCell style={{ width: 48 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((row, i) => {
              const itemName = `${profItem}.${i}`;

              return (
                <TableRow key={itemName}>
                  <TableCell style={{ fontWeight: "bold" }}>{row.name}</TableCell>
                  <TableCell align="center">
                    <Checkbox name={`${itemName}.qualified`} />
                  </TableCell>
                  <TableCell align="right">
                    <OutlinedInput
                      name={`${itemName}.yearFrom`}
                      label={locale.ExperienceYearFrom}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Checkbox name={`${itemName}.interested`} />
                  </TableCell>

                  <TableCell align="center">
                    <Text name={`${itemName}.comment`} label={locale.WriteComment} />
                  </TableCell>

                  <TableCell align="center">
                    <IconButton
                      onClick={() => deleteRow(profItem, i)}
                      disabled={row.name === profession}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Grid>
    </Paper>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  paper: {
    padding: 16,
    position: "relative"
  },

  paperTable: {}
}));
